<main class="text-start">
  <h1 class="page-title">
    <a [routerLink]="['/drivers']" class="text-[#8A8AA3] me-0.5 ms-2 hover:text-purple-800">
      <i class="bi" [class.bi-chevron-right]="languageService.isRtl()" [class.bi-chevron-left]="!languageService.isRtl()"></i>
      {{ 'drivers_management.details.back' | translate }}
    </a>
    {{ 'drivers_management.details.title' | translate }}
  </h1>

  <div class="w-full bg-[#F6F6F6] rounded-lg p-3.5 space-y-3.5">

    <!-- Profile header -->
    <div *ngIf="loading" class="bg-white rounded-xl p-5 flex max-md:flex-col-reverse items-center gap-5">
      <div class="w-24 h-24 rounded-full bg-gray-200 animate-pulse shrink-0"></div>
      <div class="flex-1 w-full text-start space-y-2">
        <div class="h-5 w-1/3 bg-gray-200 rounded animate-pulse ms-auto"></div>
        <div class="h-3 w-1/4 bg-gray-200 rounded animate-pulse ms-auto"></div>
      </div>
      <div class="h-9 w-32 bg-gray-200 rounded-lg animate-pulse md:ms-auto"></div>
    </div>

    <div *ngIf="!loading && driver" class="bg-white rounded-xl p-5 flex max-md:flex-col-reverse items-center gap-5">
      <div class="w-24 h-24 rounded-full overflow-hidden bg-[#FCC5F3] shrink-0">
        <img [src]="driver.profile_image || 'dashboard/place-holders/avatar-female.png'" class="w-full h-full object-cover" alt="" />
      </div>

      <div class="flex-1 w-full text-start">
        <p class="text-lg font-semibold text-[#121217]">{{ driver.name }}</p>
        <p class="text-sm text-[#6C6C89] mt-0.5">{{ driver.email ?? '—' }}</p>
        <div class="inline-flex items-center gap-2 mt-2">
          <button type="button"
            [disabled]="togglingActive"
            (click)="toggleActive()"
            class="relative inline-flex items-center select-none cursor-pointer disabled:cursor-wait">
            <div class="w-8 h-4 rounded-full transition-colors duration-300"
              [ngClass]="driver.is_active ? 'bg-green-500' : 'bg-gray-300'">
            </div>
            <div class="absolute start-0.5 top-0.5 w-3 h-3 bg-white rounded-full transition-transform duration-300"
              [ngStyle]="{ transform: driver.is_active ? (languageService.isRtl() ? 'translateX(-16px)' : 'translateX(16px)') : 'translateX(0)' }">
            </div>
          </button>
          <span class="text-[0.7rem]" [ngClass]="driver.is_active ? 'text-green-700' : 'text-gray-500'">
            {{ (driver.is_active ? 'drivers_management.list.status.active' : 'drivers_management.list.status.inactive') | translate }}
          </span>
        </div>
      </div>

      <div class="md:ms-auto md:self-start">
        <a [routerLink]="['/drivers', driver.id, 'edit']"
          class="inline-flex items-center gap-2 whitespace-nowrap bg-[#611F79] hover:bg-[#4d1860] text-white text-xs px-4 h-9 rounded-lg transition-colors">
          {{ 'drivers_management.details.edit_button' | translate }} <i class="bi bi-pencil-square"></i>
        </a>
      </div>
    </div>

    <!-- Personal info -->
    <div *ngIf="!loading && driver" class="bg-white rounded-xl p-5">
      <p class="text-sm font-semibold mb-4">{{ 'drivers_management.details.personal_section' | translate }}</p>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <p class="text-xs text-[#8E8E8E] mb-1">{{ 'drivers_management.details.field.first_name_ar' | translate }}</p>
          <p class="text-sm text-[#121217]">{{ driver.first_name_ar || '—' }}</p>
        </div>
        <div>
          <p class="text-xs text-[#8E8E8E] mb-1">{{ 'drivers_management.details.field.last_name_ar' | translate }}</p>
          <p class="text-sm text-[#121217]">{{ driver.last_name_ar || '—' }}</p>
        </div>
        <div>
          <p class="text-xs text-[#8E8E8E] mb-1">{{ 'drivers_management.details.field.first_name' | translate }}</p>
          <p class="text-sm text-[#121217]">{{ driver.first_name || '—' }}</p>
        </div>
        <div>
          <p class="text-xs text-[#8E8E8E] mb-1">{{ 'drivers_management.details.field.last_name' | translate }}</p>
          <p class="text-sm text-[#121217]">{{ driver.last_name || '—' }}</p>
        </div>
      </div>
    </div>

    <!-- Contact + account info -->
    <div *ngIf="!loading && driver" class="bg-white rounded-xl p-5">
      <p class="text-sm font-semibold mb-4">{{ 'drivers_management.details.contact_section' | translate }}</p>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <p class="text-xs text-[#8E8E8E] mb-1">{{ 'drivers_management.details.field.phone' | translate }}</p>
          <p class="text-sm text-[#121217]">{{ driver.phone_number ?? '—' }}</p>
        </div>
        <div>
          <p class="text-xs text-[#8E8E8E] mb-1">{{ 'drivers_management.details.field.email' | translate }}</p>
          <p class="text-sm text-[#121217]">{{ driver.email ?? '—' }}</p>
        </div>
        <div>
          <p class="text-xs text-[#8E8E8E] mb-1">{{ 'drivers_management.details.field.active_deliveries' | translate }}</p>
          <p class="text-sm text-[#121217]">{{ driver.assigned_deliveries_count }}</p>
        </div>
        <div>
          <p class="text-xs text-[#8E8E8E] mb-1">{{ 'drivers_management.details.field.date_joined' | translate }}</p>
          <p class="text-sm text-[#121217]">{{ driver.date_joined | date: 'medium' }}</p>
        </div>
      </div>
    </div>

    <!-- History / deliveries tabs -->
    <div *ngIf="!loading && driver" class="bg-white rounded-xl p-5">
      <div class="flex gap-2 border-b border-[#EEE] mb-4">
        <button type="button" (click)="switchTab('history')"
          class="px-3 py-2 text-xs font-medium border-b-2 -mb-px transition-colors"
          [ngClass]="activeTab === 'history' ? 'border-[#611F79] text-[#611F79]' : 'border-transparent text-[#8E8E8E]'">
          {{ 'drivers_management.details.tabs.history' | translate }}
        </button>
        <button type="button" (click)="switchTab('deliveries')"
          class="px-3 py-2 text-xs font-medium border-b-2 -mb-px transition-colors"
          [ngClass]="activeTab === 'deliveries' ? 'border-[#611F79] text-[#611F79]' : 'border-transparent text-[#8E8E8E]'">
          {{ 'drivers_management.details.tabs.deliveries' | translate }}
        </button>
      </div>

      <!-- Assignment history -->
      <div *ngIf="activeTab === 'history'">
        <div class="overflow-x-auto">
          <table class="generic-table text-nowrap">
            <thead>
              <tr>
                <th>{{ 'drivers_management.details.history_table.order' | translate }}</th>
                <th>{{ 'drivers_management.details.history_table.recipient' | translate }}</th>
                <th>{{ 'drivers_management.details.history_table.status' | translate }}</th>
                <th>{{ 'drivers_management.details.history_table.source' | translate }}</th>
                <th>{{ 'drivers_management.details.history_table.assigned_by' | translate }}</th>
                <th>{{ 'drivers_management.details.history_table.date' | translate }}</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let row of assignments?.results">
                <td class="text-gray-600">#{{ row.order_uuid | slice:0:8 }}</td>
                <td>{{ row.recipient_name }}</td>
                <td><span class="status-chip bg-blue-100 text-blue-700">{{ ('drivers_management.order_status.' + row.order_status) | translate }}</span></td>
                <td>{{ ('drivers_management.assignment_source.' + row.assignment_source) | translate }}</td>
                <td>{{ row.assigned_by_name ?? '—' }}</td>
                <td>{{ row.created_at | date: 'medium' }}</td>
              </tr>
              <tr *ngIf="assignments && assignments.results.length === 0">
                <td colspan="100%" class="text-start text-xs text-[#8E8E8E]">{{ 'drivers_management.details.history_table.empty' | translate }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <app-table-pagination *ngIf="assignments" [count]="assignments.count" [limit]="assignmentsPagination.limit" [offset]="assignmentsPagination.offset"
          (pageChange)="updateAssignmentsPagination($event)" />
      </div>

      <!-- Deliveries -->
      <div *ngIf="activeTab === 'deliveries'">
        <div class="overflow-x-auto">
          <table class="generic-table text-nowrap">
            <thead>
              <tr>
                <th>{{ 'drivers_management.details.deliveries_table.order' | translate }}</th>
                <th>{{ 'drivers_management.details.deliveries_table.recipient' | translate }}</th>
                <th>{{ 'drivers_management.details.deliveries_table.city' | translate }}</th>
                <th>{{ 'drivers_management.details.deliveries_table.status' | translate }}</th>
                <th>{{ 'drivers_management.details.deliveries_table.total' | translate }}</th>
                <th>{{ 'drivers_management.details.deliveries_table.date' | translate }}</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let row of deliveries?.results">
                <td class="text-gray-600">#{{ row.order_uuid | slice:0:8 }}</td>
                <td>{{ row.recipient_name }}</td>
                <td>{{ row.recipient_city }}</td>
                <td><span class="status-chip bg-blue-100 text-blue-700">{{ ('drivers_management.order_status.' + row.status) | translate }}</span></td>
                <td>{{ row.total_price }} {{ row.currency }}</td>
                <td>{{ row.placed_at | date: 'medium' }}</td>
              </tr>
              <tr *ngIf="deliveries && deliveries.results.length === 0">
                <td colspan="100%" class="text-start text-xs text-[#8E8E8E]">{{ 'drivers_management.details.deliveries_table.empty' | translate }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <app-table-pagination *ngIf="deliveries" [count]="deliveries.count" [limit]="deliveriesPagination.limit" [offset]="deliveriesPagination.offset"
          (pageChange)="updateDeliveriesPagination($event)" />
      </div>
    </div>

  </div>
</main>
